/**
 * Mapeia respostas do webhook `colaboradores` (action get, type all) e linhas Supabase
 * para o formato da tela de Colaboradores.
 */

import {
  buildColaboradorCargoJson,
  parseColaboradorCargoJson,
  type ColaboradorCargoJson,
} from "../types/collaboratorCargo";

export type ColaboradorOnboardingStatus = "ready" | "pending" | "expired";

export type ColaboradorUi = {
  id: string;
  nome: string;
  cargo: string;
  cargoMeta?: ColaboradorCargoJson;
  cpf?: string;
  telefone: string;
  email?: string;
  status: "ativo" | "inativo" | "ferias" | "licenca";
  observacoes?: string;
  diaria?: number;
  passwordSet?: boolean;
  inviteExpiresAt?: string;
  inviteCreatedAt?: string;
  inviteExpired?: boolean;
  inviteToken?: string;
  onboardingStatus?: ColaboradorOnboardingStatus;
  /** Agenda profissional (Google individual) ligada. */
  agendaEnabled?: boolean;
  /** Área/especialidade (categoria de serviços). */
  agendaArea?: string;
  /** CNPJs das lojas do grupo em que atua. Vazio = só a loja atual. */
  storeCnpjs?: string[];
};

export function resolveColaboradorOnboardingStatus(row: {
  passwordSet?: boolean;
  inviteExpiresAt?: string;
  inviteExpired?: boolean;
}): ColaboradorOnboardingStatus {
  if (row.passwordSet) return "ready";
  if (row.inviteExpiresAt && !row.inviteExpired) {
    const end = new Date(row.inviteExpiresAt).getTime();
    if (Number.isFinite(end) && end > Date.now()) return "pending";
  }
  return "expired";
}

function parseCargoFromRow(row: Record<string, unknown>, tituloFallback: string): ColaboradorCargoJson {
  const raw = row.cargo;
  if (raw == null) {
    return buildColaboradorCargoJson(tituloFallback);
  }
  if (typeof raw === "string") {
    try {
      const p = parseColaboradorCargoJson(JSON.parse(raw));
      if (p) return p;
    } catch {
      /* ignore */
    }
    return buildColaboradorCargoJson(tituloFallback);
  }
  if (typeof raw === "object") {
    const p = parseColaboradorCargoJson(raw);
    if (p) return p;
  }
  return buildColaboradorCargoJson(tituloFallback);
}

function parsePositiveMoney(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw !== "string") return undefined;
  const s = raw.trim();
  if (!s) return undefined;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  const normalized =
    lastComma > lastDot ? s.replace(/\./g, "").replace(",", ".") : s.replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function cargoRecordFromRow(row: Record<string, unknown>): Record<string, unknown> | null {
  const raw = row.cargo ?? row.role;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* cargo legado em texto */
    }
  }
  return null;
}

/** Diária do entregador — top-level `diaria` ou dentro do json `cargo`/`role`. */
export function parseColaboradorDiaria(row: Record<string, unknown>): number | undefined {
  const top = parsePositiveMoney(row.diaria);
  if (top != null) return top;
  const cargo = cargoRecordFromRow(row);
  return cargo ? parsePositiveMoney(cargo.diaria) : undefined;
}

/** Converte uma linha da tabela `colaboradores` (ou payload próximo) para o card da UI. */
export function mapColaboradorRowToUi(row: Record<string, unknown>): ColaboradorUi {
  const id = String(row.id ?? "").trim();
  const nome = String(row.nome_completo ?? row.nome ?? "").trim() || "Colaborador";
  const cpfDigits = String(row.cpf ?? "").replace(/\D/g, "");
  const cpf = cpfDigits.length === 11 ? cpfDigits : undefined;
  const telefoneRaw = String(row.telefone ?? "");
  const email = typeof row.email === "string" ? row.email : "";
  const ativo =
    row.ativo === true ||
    row.ativo === "true" ||
    row.ativo === 1 ||
    String(row.ativo).toLowerCase() === "ativo";
  const tituloFromCargo =
    typeof row.cargo === "object" && row.cargo !== null && "titulo" in (row.cargo as object)
      ? String((row.cargo as { titulo?: string }).titulo || "").trim()
      : "";
  const cargoTitulo = tituloFromCargo || "Colaborador";
  const cargoMeta = parseCargoFromRow(row, cargoTitulo);
  const passwordSet =
    row.password_set === true ||
    row.password_set === "true" ||
    row.senha_definida === true ||
    row.senha_definida === "true";
  const inviteExpiresAt =
    typeof row.invite_expires_at === "string" ? row.invite_expires_at : undefined;
  const inviteCreatedAt =
    typeof row.invite_created_at === "string" ? row.invite_created_at : undefined;
  const inviteExpired = row.invite_expired === true || row.invite_expired === "true";
  const inviteToken =
    typeof row.invite_token === "string" && row.invite_token.trim()
      ? row.invite_token.trim()
      : undefined;
  const onboardingStatus = resolveColaboradorOnboardingStatus({
    passwordSet,
    inviteExpiresAt,
    inviteExpired,
  });

  const agendaEnabled =
    row.agenda_enabled === true ||
    row.agenda_enabled === "true" ||
    row.agenda_enabled === 1;
  const agendaArea =
    typeof row.agenda_area === "string" && row.agenda_area.trim()
      ? row.agenda_area.trim()
      : undefined;
  const storeCnpjs = Array.isArray(row.kitchen_stores)
    ? row.kitchen_stores
        .map((item) => String(item || "").replace(/\D/g, ""))
        .filter((item) => item.length === 14)
    : [];

  return {
    id: id || `temp-${cpfDigits || nome}`,
    nome,
    cargo: cargoMeta.titulo || cargoTitulo,
    cargoMeta,
    cpf: cpfDigits.length === 11 ? cpfDigits : undefined,
    telefone: telefoneRaw,
    email: email || undefined,
    status: ativo ? "ativo" : "inativo",
    observacoes: typeof row.observacoes === "string" ? row.observacoes : undefined,
    diaria: parseColaboradorDiaria(row),
    passwordSet,
    inviteExpiresAt,
    inviteCreatedAt,
    inviteExpired,
    inviteToken,
    onboardingStatus,
    agendaEnabled,
    agendaArea,
    storeCnpjs,
  };
}

/**
 * Interpreta o JSON do n8n para `action: get` + `type: all`.
 * Contrato recomendado: `{ ok: true, colaboradores: [ ...linhas ] }`.
 * Aceita também `rows`, `data` ou array na raiz.
 */
export function parseCollaboratorsListResponse(raw: unknown): Record<string, unknown>[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((x) => x && typeof x === "object") as Record<string, unknown>[];
  }
  if (typeof raw !== "object") return [];
  const r = raw as Record<string, unknown>;

  const pickArray = (v: unknown): unknown[] | null =>
    Array.isArray(v) ? v : null;

  const fromColab = pickArray(r.colaboradores);
  if (fromColab) return fromColab.filter((x) => x && typeof x === "object") as Record<string, unknown>[];

  const fromRows = pickArray(r.rows);
  if (fromRows) return fromRows.filter((x) => x && typeof x === "object") as Record<string, unknown>[];

  const fromData = pickArray(r.data);
  if (fromData) return fromData.filter((x) => x && typeof x === "object") as Record<string, unknown>[];

  const fromItems = pickArray(r.items);
  if (fromItems) return fromItems.filter((x) => x && typeof x === "object") as Record<string, unknown>[];

  // Um único objeto estilo login: empresa embutida
  if (r.exists === true && r.empresa && typeof r.empresa === "object") {
    const e = r.empresa as Record<string, unknown>;
    return [e];
  }

  return [];
}
